import { Injectable } from '@angular/core';
import { WlContainer } from './wl.service';

@Injectable({
  providedIn: 'root',
})
export class MetadataService {
  private defaultFavicon = '/assets/icon/icon.svg';
  private defaultTitle = 'EnrollHere';
  private defaultDescription =
    'EnrollHere delivers an end-to-end, fully integrated platform that empowers businesses, agencies, and carriers with resources and tools to manage and grow. Simplify processes, optimize costs, and scale your organization—from leads to commissions';
  private currentFavicon: string | null = null;
  private currentTitle: string | null = null;
  private currentDescription: string | null = null;

  updateMetadata(wl: WlContainer | undefined): void {
    if (!wl) {
      this.updateFavicon(undefined);
      this.updateTitle(undefined);
      this.updateDescription(undefined);
      return;
    }

    this.updateFavicon(wl);
    this.updateTitle(wl);
    this.updateDescription(wl);
  }

  private updateFavicon(wl: WlContainer | undefined): void {
    let faviconUrl: string;

    if (wl && wl.meta?.favicon) {
      faviconUrl = wl.meta.favicon;
    } else {
      faviconUrl = this.defaultFavicon;
    }

    if (!this.isValidUrl(faviconUrl)) {
      console.warn(`Invalid favicon URL: ${faviconUrl}, using default`);
      faviconUrl = this.defaultFavicon;
    }

    if (faviconUrl === this.currentFavicon) {
      return;
    }
    try {
      const existingLinks = document.querySelectorAll('link[rel*="icon"]');
      existingLinks.forEach(link => link.remove());

      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = faviconUrl;
      link.type = this.getMimeType(faviconUrl);

      document.head.appendChild(link);
      this.currentFavicon = faviconUrl;
    } catch (error) {
      console.error('Failed to update favicon:', error);
    }
  }

  private updateTitle(wl: WlContainer | undefined): void {
    const title = wl?.meta?.title?.trim() || this.defaultTitle;

    if (title === this.currentTitle) {
      return;
    }

    try {
      document.title = title;
      this.currentTitle = title;
    } catch (error) {
      console.error('Failed to update title:', error);
    }
  }

  private updateDescription(wl: WlContainer | undefined): void {
    const description =
      wl?.meta?.description?.trim() || this.defaultDescription;

    if (description === this.currentDescription) {
      return;
    }

    try {
      const existingMetaTags = document.querySelectorAll(
        'meta[name="description"]'
      );
      existingMetaTags.forEach(tag => tag.remove());

      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = description;

      document.head.appendChild(meta);
      this.currentDescription = description;
    } catch (error) {
      console.error('Failed to update description:', error);
    }
  }

  private isValidUrl(url: string): boolean {
    try {
      new URL(url, window.location.origin);
      return true;
    } catch {
      return false;
    }
  }

  private getMimeType(url: string): string {
    try {
      const cleanUrl = url.split('?')[0];
      const extension = cleanUrl.split('.').pop()?.toLowerCase();

      switch (extension) {
        case 'svg':
          return 'image/svg+xml';
        case 'png':
          return 'image/png';
        case 'jpg':
        case 'jpeg':
          return 'image/jpeg';
        case 'ico':
          return 'image/x-icon';
        default:
          return 'image/svg+xml';
      }
    } catch {
      return 'image/svg+xml';
    }
  }

  resetToDefault(): void {
    this.updateMetadata(undefined);
  }
}
